<template>
  <div class="footer-consent-tab" v-if="initialized">
    <div class="pa-5 pb-12">

      <div class="label font-weight-bold">{{ $t('footer_consent_footer_section') }}</div>

      <v-switch v-if="isAdmin" v-model="footerHide" :label="$t('footer_hide_label')" hide-details class="mt-4 d-inline-flex"></v-switch>

      <div class="sub-label mt-4">{{ $t('footer_body_label') }}</div>
      <text-edit :key="footerBodyEditorKey" class="footer-body-edit" :class="{ 'disabled-input': footerHide || !isAdmin }" :content="footerBody" :is-translation-edit="true" :disable-rich-text="footerHide || !isAdmin" :disable-variables="true" :placeholder="$t('scanresult_powered_by_scantrust')" @update:content="setFooterBody"></text-edit>

      <div class="sub-label mt-4">{{ $t('footer_background_label') }}</div>
      <div class="d-flex align-center">
        <v-text-field v-model="footerBackground" hide-details="auto" class="mt-0 pt-0" :disabled="footerHide" :rules="urlRules" placeholder="https://…/footer-bg.png" @focus="urlBeforeEdit = savedUrl('footer', 'background')" @change="clearUrlLanguageCopies('footer', 'background')"></v-text-field>
        <v-tooltip top v-if="isAllowed('gallery_view')">
          <template v-slot:activator="{ on, attrs }">
            <v-btn v-bind="attrs" v-on="on" icon color="primary" class="ml-2" :disabled="footerHide" @click="displaySTGalleryDialog = true">
              <v-icon>mdi-image-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('translations_select_image') }}</span>
        </v-tooltip>
      </div>

      <div class="sub-label mt-7">{{ $t('footer_privacy_url_label') }}</div>
      <v-text-field v-model="footerPrivacyUrl" hide-details="auto" class="mt-0 pt-0" :rules="urlRules" placeholder="https://www.scantrust.com/privacy-policy" @focus="urlBeforeEdit = savedUrl('footer', 'privacy')" @change="clearUrlLanguageCopies('footer', 'privacy')"></v-text-field>

      <div class="sub-label mt-7">{{ $t('footer_privacy_button_label') }}</div>
      <v-text-field v-model="footerPrivacyButton" hide-details class="mt-0 pt-0" placeholder="Privacy Policy"></v-text-field>

      <div class="sub-label mt-7">{{ $t('footer_cookie_button_label') }}</div>
      <v-text-field v-model="footerCookieButton" hide-details class="mt-0 pt-0" :disabled="footerHide" placeholder="Cookie Policy"></v-text-field>

      <div class="label font-weight-bold section-header-gap">{{ $t('footer_consent_cookie_section') }}</div>

      <div class="sub-label mt-4">{{ $t('cookie_message_label') }}</div>
      <v-textarea v-model="cookieMessage" hide-details class="mt-0 pt-0" rows="1" auto-grow placeholder="We use necessary cookies…" @keydown.enter.prevent></v-textarea>

      <div class="sub-label mt-6">{{ $t('cookie_accept_label') }}</div>
      <v-text-field v-model="cookieAccept" hide-details class="mt-0 pt-0" placeholder="Accept cookies"></v-text-field>

      <div class="sub-label mt-7">{{ $t('cookie_decline_label') }}</div>
      <v-text-field v-model="cookieDecline" hide-details class="mt-0 pt-0" placeholder="Decline"></v-text-field>

      <div class="sub-label mt-7">{{ $t('cookie_policy_label') }}</div>
      <v-text-field v-model="cookiePolicy" hide-details class="mt-0 pt-0" placeholder="Cookie Policy"></v-text-field>

      <div class="sub-label mt-7">{{ $t('cookie_link_label') }}</div>
      <v-text-field v-model="cookieLink" hide-details="auto" class="mt-0 pt-0" :rules="urlRules" placeholder="https://www.scantrust.com/privacy-policy/" @focus="urlBeforeEdit = savedUrl('cookie', 'link')" @change="clearUrlLanguageCopies('cookie', 'link')"></v-text-field>

    </div>

    <select-image-dialog
      :display-select-image-dialog="displaySTGalleryDialog"
      @update:displaySelectImageDialog="displaySTGalleryDialog = false"
      @gallery-selected="handleSTGallerySelected"
      :app-tags="['stc-editor-image']"
      :isAllowedEdit="isAllowed('gallery_edit')"
    ></select-image-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import TextEdit from '@/components/editor/TextEdit'
import { SelectImageDialog } from '@scantrust/st-gallery'

export default {
  name: 'FooterConsentTab',
  components: {
    TextEdit,
    SelectImageDialog
  },
  computed: {
    ...mapGetters([
      'initialized',
      'stcConfig',
      'getFooter',
      'getCookie',
      'defaultLanguage',
      'isAllowed',
      'isAdmin'
    ]),
    footerBody: {
      get () {
        return (this.getFooter.body && this.getFooter.body[this.defaultLanguage]) || ''
      },
      set (value) {
        this.setFooterBody(value)
      }
    },
    footerBackground: {
      get () {
        const background = this.getFooter.background
        if (typeof background === 'string') {
          return background
        }
        return (background && background[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_FOOTER_LOCALE_FIELD', { field: 'background', lang: this.defaultLanguage, value })
      }
    },
    footerHide: {
      get () {
        return !!this.getFooter.hide
      },
      set (value) {
        this.$store.commit('UPDATE_FOOTER_SETTING', { field: 'hide', value })
      }
    },
    footerPrivacyUrl: {
      get () {
        return (this.getFooter.privacy && this.getFooter.privacy[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_FOOTER_LOCALE_FIELD', { field: 'privacy', lang: this.defaultLanguage, value })
      }
    },
    footerPrivacyButton: {
      get () {
        const gdpr = this.getFooter.gdpr
        return (gdpr && gdpr.privacy && gdpr.privacy[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_FOOTER_GDPR_FIELD', { field: 'privacy', lang: this.defaultLanguage, value })
      }
    },
    footerCookieButton: {
      get () {
        const gdpr = this.getFooter.gdpr
        return (gdpr && gdpr.cookie && gdpr.cookie[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_FOOTER_GDPR_FIELD', { field: 'cookie', lang: this.defaultLanguage, value })
      }
    },
    cookieMessage: {
      get () {
        return (this.getCookie.message && this.getCookie.message[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_COOKIE_FIELD', { field: 'message', lang: this.defaultLanguage, value })
      }
    },
    cookieAccept: {
      get () {
        return (this.getCookie.accept && this.getCookie.accept[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_COOKIE_FIELD', { field: 'accept', lang: this.defaultLanguage, value })
      }
    },
    cookieDecline: {
      get () {
        return (this.getCookie.decline && this.getCookie.decline[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_COOKIE_FIELD', { field: 'decline', lang: this.defaultLanguage, value })
      }
    },
    cookiePolicy: {
      get () {
        return (this.getCookie.policy && this.getCookie.policy[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_COOKIE_FIELD', { field: 'policy', lang: this.defaultLanguage, value })
      }
    },
    cookieLink: {
      get () {
        const link = this.getCookie.link
        if (typeof link === 'string') {
          return link
        }
        return (link && link[this.defaultLanguage]) || ''
      },
      set (value) {
        this.$store.commit('UPDATE_COOKIE_FIELD', { field: 'link', lang: this.defaultLanguage, value })
      }
    }
  },
  watch: {
    stcConfig () {
      this.footerBodyEditorKey += 1
    }
  },
  data () {
    return {
      displaySTGalleryDialog: false,
      // The footerBodyEditorKey is needed to update/sync the new footer field (which is a custom rich text field) when auto-update triggers.
      footerBodyEditorKey: 0,
      // Remembers the link a URL field started with, so that deleting the link can also
      // remove it from the other languages
      urlBeforeEdit: '',
      urlRules: [
        v => (!v || /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(v)) || this.$t('url_validation')
      ]
    }
  },
  methods: {
    setFooterBody (value) {
      this.$store.commit('UPDATE_FOOTER_LOCALE_FIELD', { field: 'body', lang: this.defaultLanguage, value, richText: true })
    },

    handleSTGallerySelected (url) {
      this.footerBackground = url
    },

    savedUrl (section, field) {
      const saved = section === 'footer' ? this.getFooter[field] : this.getCookie[field]
      return typeof saved === 'string' ? saved : (saved && saved[this.defaultLanguage]) || ''
    },

    // Deleting a link here also deletes it from the languages that were showing the same
    // one, so that none of them keeps opening a link the user removed. Waits until the
    // user blurs the field.
    clearUrlLanguageCopies (section, field) {
      if (this.savedUrl(section, field)) {
        return
      }
      this.$store.commit('CLEAR_URL_LANGUAGE_COPIES', { section, field, deletedUrl: this.urlBeforeEdit })
    }
  }
}
</script>

<style lang="sass" scoped>

@import '@/variables.sass'

.footer-consent-tab
  height: calc(100% - 52px)
  overflow: auto

.label
  text-transform: uppercase
  color: $label-grey

.sub-label
  color: $label-grey

.section-header-gap
  margin-top: 72px

.footer-body-edit
  ::v-deep .rich-input
    padding-left: 0 !important
  ::v-deep .ProseMirror
    min-height: 28px
    p
      margin: 0
    p.is-editor-empty:first-child::before
      font-style: normal

.disabled-input
  opacity: 0.5
  pointer-events: none

</style>
